import { ReactNode } from "react"

import { IconName } from "hived-theme/lib/iconList"

import Icon from "@/components/Icon"

interface EntityDetailProps {
  icon: IconName
  title: ReactNode
  /** Rendered beside the title - badges, status warnings. */
  titleAdornments?: ReactNode
  /** Laid out as a three-column grid of summary rows. */
  summary: ReactNode
  onClose: () => void
  children: ReactNode
}

export function EntityDetail({
  icon,
  title,
  titleAdornments,
  summary,
  onClose,
  children,
}: EntityDetailProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="bg-white p-6">
        <div className="mb-4 flex items-center space-x-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-palette-jungleGreen100">
            <Icon name={icon} className="h-7 w-7 text-palette-jungleGreen900" />
          </div>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
            <h2 className="text-base font-bold text-palette-jungleGreen900">{title}</h2>
            {titleAdornments}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-palette-jungleGreen400 transition-colors hover:text-palette-jungleGreen700"
            aria-label="Close details"
          >
            <Icon name="close" className="h-5 w-5 text-current" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-x-4 gap-y-3">{summary}</div>
      </div>

      <div className="flex-1 p-6 pt-0">{children}</div>
    </div>
  )
}

export function EntityDetailColumn({ children }: { children: ReactNode }) {
  return <div className="min-w-0 space-y-3">{children}</div>
}

interface EntityDetailRowProps {
  icon: IconName
  label?: string
  onClick?: () => void
  children: ReactNode
}

export function EntityDetailRow({ icon, label, onClick, children }: EntityDetailRowProps) {
  return (
    <div
      className={`flex items-start space-x-2 ${onClick ? "cursor-pointer" : ""}`}
      onClick={onClick}
    >
      <Icon name={icon} className="mt-0.5 h-4 w-4 shrink-0 text-palette-jungleGreen400" />
      {label && <span className="text-sm text-palette-jungleGreen600">{label}:</span>}
      <span className="min-w-0 text-sm text-palette-jungleGreen900">{children}</span>
    </div>
  )
}
